<template>
  <div v-if="!question.subQuestions" class="flex min-h-0 flex-1 flex-col pb-4 lg:justify-center">
    <div
      class="xs:py-6 flex flex-row items-end pb-8 lg:grow-0 lg:items-end"
      :class="feedbackType === FeedbackType.Textual ? 'shrink-0' : 'grow'"
    >
      <h3 class="text-headline-small md:text-headline-medium w-full px-2 text-center text-balance">
        {{ question.content }}
      </h3>
    </div>
    <ScaleBasedQuestion
      v-if="
        feedbackType == FeedbackType.ScaleBased || feedbackType == FeedbackType.TextualAndScaleBased
      "
      :key="question.id"
      class="shrink-0"
      :model-value="feedback?.value"
      :type="feedbackType"
      :question-id="question.id"
      :scale="scale!"
      :scale-item-descriptions="question.scaleItemDescriptions"
      :current-selected-index="currentSelectedIndex"
      @update:model-value="(value) => updateValue(value)"
    />
    <TextualQuestion
      v-if="feedbackType == FeedbackType.Textual"
      class="min-h-0 flex-1 lg:flex-none"
      :model-value="feedback?.text"
      @update:model-value="(text) => updateText(text)"
      :auto-focus-enabled="autoFocusEnabled"
    />
    <AdditionalTextFeedback
      v-if="feedbackType == FeedbackType.TextualAndScaleBased"
      class="shrink-0"
      :question-id="question.id"
      :text="feedback?.text"
      @update:text="(text) => updateText(text)"
      :has-value="hasValue"
    />
  </div>
  <SingleQuestionWithSubQuestions
    v-else-if="scale && scale.type == ScaleType.ImageButtons"
    :question="question"
    :skills="question.skills"
    :scale="scale"
    :key="question.id"
    :model-value="feedback?.value"
    @update:model-value="(value) => updateValue(value)"
    :sub-feedbacks="feedback?.subFeedbacks"
    @update:sub-feedbacks="(subFeedbacks) => updateSubFeedbacks(subFeedbacks)"
  />
</template>
<script lang="ts" setup>
  import type { FeedbackLabel } from '@/core/models/feedback-label/feedback-label'
  import { ScaleType, type Scale } from '@/core/models/scale'
  import { FeedbackType } from '@/core/models/feedback-type'
  import ScaleBasedQuestion from '../scale-based-questions/ScaleBasedQuestion.vue'
  import TextualQuestion from '../textual-questions/TextualQuestion.vue'
  import type { JSONContent } from '@tiptap/vue-3'
  import { computed } from 'vue'
  import SingleQuestionWithSubQuestions from './SingleQuestionWithSubQuestions.vue'
  import AdditionalTextFeedback from './AdditionalTextFeedback.vue'
  import type { Skill } from '@/core/models/skill.ts'

  interface Props {
    question: {
      readonly id: string
      readonly content: string
      readonly scaleItemDescriptions?: ReadonlyArray<string>
      readonly skills: ReadonlyArray<Skill>
      readonly subQuestions?: ReadonlyArray<{
        readonly id: string
        readonly content: string
        readonly scaleItemDescriptions?: ReadonlyArray<string>
      }>
    }
    questionGroupId: string
    feedbackType: FeedbackType
    scale?: Scale
    labels: FeedbackLabel[]
    autoFocusEnabled: boolean
    feedback?: {
      value?: number
      text?: JSONContent
      subFeedbacks?: { questionId: string; value: number }[]
    }
  }

  // const { isOpen, closeDialog, openDialog } = useDialog()

  const emits = defineEmits<{
    (
      e: 'update:feedback',
      feedback: {
        value?: number
        text?: JSONContent
        additionalTextDraft?: JSONContent
        subFeedbacks?: { questionId: string; value: number }[]
      },
    ): void
  }>()

  const props = defineProps<Props>()

  const updateValue = (value?: number) => {
    emits('update:feedback', { ...props.feedback, value })
  }

  const updateSubFeedbacks = (subFeedbacks: { questionId: string; value: number }[]) => {
    emits('update:feedback', { ...props.feedback, subFeedbacks })
  }

  const updateText = (text?: JSONContent) => {
    emits('update:feedback', { ...props.feedback, text })
  }

  const currentSelectedIndex = computed(() => {
    const index = props.scale?.items.findIndex((item) => item.value == props.feedback?.value)

    if (index && index >= 0) return index

    return 0
  })

  const hasValue = computed(() => {
    return !!props.feedback && props.feedback.value != undefined
  })

  // const hasLabels = computed(() => props.labels.length > 0)

  // const labelKeys = computed(() => {
  //   return feedback.value.labels.map((label) => label.key)
  // })

  // const updateLabel = (label: FeedbackLabel) => {
  //   if (labelKeys.value.includes(label.key)) {
  //     feedback.value.labels = feedback.value.labels.filter((l) => l.key !== label.key)
  //   } else {
  //     feedback.value.labels.push(label)
  //   }

  //   emit('update:modelValue', feedback.value)
  // }
</script>
